import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { useNavigate, useLocation } from 'react-router-dom';
import '../css/ParticipantList.css';
import '../css/SearchTable.css';
import axios from 'axios';
import AssignParticipantsModal from './AssignParticipantsModal';
import { DataTableLoader, GoldLoader } from '../shared/Loaders';
import OpenAssignedProfileModal from './OpenAssignedProfileModal';
import { resolvePersonImageUrl } from '../../utils/resolvePersonImageUrl';

/** Header + body same padding/widths so columns line up */
const searchTableStyles = {
  table: {
    style: {
      backgroundColor: '#FFFBF4',
    },
  },
  headRow: {
    style: {
      backgroundColor: '#D9A128',
      minHeight: '52px',
      borderBottom: 'none',
    },
  },
  headCells: {
    style: {
      color: '#ffffff',
      fontWeight: '600',
      backgroundColor: '#D9A128',
      justifyContent: 'flex-start',
      textAlign: 'left',
      paddingLeft: '12px',
      paddingRight: '12px',
      borderRight: 'none',
    },
  },
  rows: {
    style: {
      backgroundColor: '#FFFBF4',
      minHeight: '56px',
      borderBottom: '1px solid #eee',
    },
  },
  cells: {
    style: {
      backgroundColor: '#FFFBF4',
      color: '#333',
      justifyContent: 'flex-start',
      textAlign: 'left',
      paddingLeft: '12px',
      paddingRight: '12px',
      borderRight: 'none',
    },
  },
  pagination: {
    style: {
      backgroundColor: '#FFFBF4',
      borderTop: '1px solid #efe6d6',
    },
  },
};

const getRegistrationBadgeClass = (status) => {
  switch (status) {
    case 'Added':
      return 'badge bg-secondary participant-badge-registration';
    case 'Pending':
      return 'badge bg-warning text-white participant-badge-registration';
    case 'Registered':
      return 'badge bg-success participant-badge-registration';
    default:
      return 'badge bg-secondary';
  }
};

const personIdOf = (participant) =>
  String(participant?.person_id?._id || participant?.person_id || '');

const personDisplayName = (person) =>
  [person?.name, person?.mname, person?.lname].filter(Boolean).join(' ') || 'Unnamed participant';

const emsParticipantPath = (participantId) =>
  `/ems/participants/edit-participant/${participantId}`;

const sameSelectedPeople = (currentRows, nextRows) => {
  if (currentRows.length !== nextRows.length) return false;
  const currentIds = new Set(currentRows.map((person) => String(person?._id)));
  return nextRows.every((person) => currentIds.has(String(person?._id)));
};

const SearchTable = () => {
  const API_URL = process.env.REACT_APP_API_URL;

  const [persons, setPersons] = useState([]);
  const [groups, setGroups] = useState([]);
  const [invitationTypes, setInvitationTypes] = useState([]);
  const [emsRoles, setEmsRoles] = useState([]);
  const [accreditationCategories, setAccreditationCategories] = useState([]);
  const [assignedParticipants, setAssignedParticipants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [profileChooserItems, setProfileChooserItems] = useState(null);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [competitionId, setCompetitionId] = useState('');
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const isSyncingSelection = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  const fetchAssignedParticipants = useCallback(async (compId) => {
    if (!compId) {
      setAssignedParticipants([]);
      return;
    }
    const participantRes = await axios.get(`${API_URL}/ems/participants?competition_id=${compId}`);
    setAssignedParticipants(Array.isArray(participantRes.data) ? participantRes.data : []);
  }, [API_URL]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          personRes,
          activecompetitionRes,
          groupRes,
          invitationTypeRes,
          emsRoleRes,
          accreditationCategoryRes,
        ] = await Promise.all([
          axios.get(`${API_URL}/persons`),
          axios.get(`${API_URL}/ems/competitions?active=true`),
          axios.get(`${API_URL}/ems/groups`),
          axios.get(`${API_URL}/ems/invitation-types`),
          axios.get(`${API_URL}/ems/roles`),
          axios.get(`${API_URL}/ems/accreditation-categories`),
        ]);

        const activecompetition = Array.isArray(activecompetitionRes.data)
          ? activecompetitionRes.data[0]
          : null;
        const compId = activecompetition?._id || '';
        setCompetitionId(compId);

        if (compId) {
          await fetchAssignedParticipants(compId);
        }

        setPersons(personRes.data || []);
        setGroups(Array.isArray(groupRes.data) ? groupRes.data : []);
        setInvitationTypes(Array.isArray(invitationTypeRes.data) ? invitationTypeRes.data : []);
        setEmsRoles(Array.isArray(emsRoleRes.data) ? emsRoleRes.data : []);
        setAccreditationCategories(
          Array.isArray(accreditationCategoryRes.data) ? accreditationCategoryRes.data : []
        );
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setGroups([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL, fetchAssignedParticipants]);

  useEffect(() => {
    if (location.state?.searchValue) {
      setSearchTerm(location.state.searchValue);
    }
  }, [location.state]);

  const participantByPersonId = useMemo(() => {
    const map = {};
    (assignedParticipants || []).forEach((p) => {
      const pid = personIdOf(p);
      if (pid) map[pid] = p;
    });
    return map;
  }, [assignedParticipants]);

  const filteredPersons = useMemo(() => {
    let filtered = Array.isArray(persons) ? [...persons] : [];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((p) => {
        const fullName = `${p?.name || ''} ${p?.mname || ''} ${p?.lname || ''}`.toLowerCase();
        return fullName.includes(term) || (p?.email || '').toLowerCase().includes(term);
      });
    }
    return filtered;
  }, [persons, searchTerm]);

  const selectedIds = useMemo(
    () => new Set(selectedRows.map((person) => String(person?._id))),
    [selectedRows]
  );
  const isRowSelected = useCallback(
    (row) => selectedIds.has(String(row?._id)),
    [selectedIds]
  );

  const openEmsProfile = useCallback(
    (participantId) => {
      if (!participantId) return;
      navigate(emsParticipantPath(participantId));
    },
    [navigate]
  );

  const handleRowClick = useCallback(
    (row) => {
      const participant = participantByPersonId[String(row?._id)];
      if (!participant?._id) return;
      openEmsProfile(participant._id);
    },
    [participantByPersonId, openEmsProfile]
  );

  const columns = [
    {
      name: 'Status',
      cell: (row) => {
        const participant = participantByPersonId[String(row?._id)];
        if (!participant) {
          return <span className="text-muted small">Not assigned</span>;
        }
        const status = participant.registration_status || 'Added';
        return (
          <span className={getRegistrationBadgeClass(status)} title="Registration status">
            {status}
          </span>
        );
      },
      width: '140px',
      grow: 0,
      ignoreRowClick: true,
    },
    {
      name: 'Image',
      cell: (row) => (
        <img
          src={resolvePersonImageUrl(row?.image, API_URL) || '/images/avatar.png'}
          alt="profile"
          style={{ width: 40, height: 40, borderRadius: '50%' }}
        />
      ),
      width: '90px',
      grow: 0,
      ignoreRowClick: true,
    },
    {
      name: 'Name',
      selector: (row) => `${row?.name || ''} ${row?.mname || ''} ${row?.lname || ''}`,
      sortable: true,
      grow: 2,
      minWidth: '180px',
      cell: (row) => {
        const isAssigned = Boolean(participantByPersonId[String(row?._id)]);
        return (
          <span
            className={`fw-semibold ${isAssigned ? 'search-name-link text-secondary' : 'text-secondary'}`}
            style={{ cursor: isAssigned ? 'pointer' : 'default' }}
            onClick={isAssigned ? () => handleRowClick(row) : undefined}
            role={isAssigned ? 'link' : undefined}
            tabIndex={isAssigned ? 0 : undefined}
            onKeyDown={
              isAssigned
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleRowClick(row);
                    }
                  }
                : undefined
            }
          >
            {row?.name} {row?.mname || ''} {row?.lname}
          </span>
        );
      },
    },
    {
      name: 'Email',
      selector: (row) => row?.email || '-',
      sortable: true,
      grow: 2,
      minWidth: '180px',
    },
    {
      name: 'Country Code',
      selector: (row) => row?.country_id?.code || '-',
      width: '130px',
      grow: 0,
    },
  ];

  const clearSelection = () => {
    isSyncingSelection.current = true;
    setSelectedRows([]);
    setClearSelectedRows((previous) => !previous);
    window.setTimeout(() => {
      isSyncingSelection.current = false;
    }, 0);
  };

  const removeSelectedParticipant = (personId) => {
    const remainingRows = selectedRows.filter((person) => String(person?._id) !== String(personId));
    isSyncingSelection.current = true;
    setSelectedRows(remainingRows);
    setClearSelectedRows((previous) => !previous);
    if (!remainingRows.length) setShowAssignModal(false);
    window.setTimeout(() => {
      isSyncingSelection.current = false;
    }, 0);
  };

  const handleAssigned = async (createdParticipants) => {
    const created = Array.isArray(createdParticipants) ? createdParticipants : [];
    const nameByPersonId = Object.fromEntries(
      selectedRows.map((person) => [String(person?._id), personDisplayName(person)])
    );

    await fetchAssignedParticipants(competitionId);
    clearSelection();
    setShowAssignModal(false);

    if (created.length === 1) {
      openEmsProfile(created[0]._id);
      return;
    }

    if (created.length > 1) {
      setProfileChooserItems(
        created.map((participant) => ({
          participantId: participant._id,
          name: nameByPersonId[personIdOf(participant)] || 'Unnamed participant',
        }))
      );
      setSuccessMessage(`${created.length} participants added successfully.`);
      return;
    }

    setSuccessMessage('No new participants were added.');
  };

  const handleChooserSelect = (participantId) => {
    setProfileChooserItems(null);
    openEmsProfile(participantId);
  };

  const goToAddParticipant = () => {
    navigate('/ems/persons/add-person', { state: { searchValue: searchTerm } });
  };

  const showEmptySearch = !loading && filteredPersons.length === 0 && searchTerm.trim() !== '';
  const showResultsCount = !loading && filteredPersons.length > 0;

  if (loading && persons.length === 0) {
    return <GoldLoader label="Loading..." variant="page" />;
  }

  return (
    <div className="search-table-container">
      <div className="pl-single-row pl-single-row--toolbar search-assign-toolbar mb-2">
        <div className="pl-single-item search-assign-search">
          <input
            type="text"
            className="form-control form-control-sm search-input"
            placeholder="Search name or email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ border: '1px solid #d1cfcf', width: '100%' }}
            aria-label="Search name or email"
          />
        </div>
        {showResultsCount && (
          <div className="search-results-count">
            {filteredPersons.length} Participant{filteredPersons.length === 1 ? '' : 's'} found
          </div>
        )}
      </div>

      {successMessage && (
        <div className="search-success-banner" role="status">
          <span>{successMessage}</span>
          <button
            type="button"
            className="search-success-banner__close"
            onClick={() => setSuccessMessage('')}
            aria-label="Dismiss success message"
          >
            ×
          </button>
        </div>
      )}

      {showEmptySearch ? (
        <div className="search-empty-state">
          <p>No individuals found for “{searchTerm.trim()}”.</p>
          <button type="button" className="btn search-assign-add-btn search-assign-add-btn--empty" onClick={goToAddParticipant}>
            + Add Participant
          </button>
        </div>
      ) : (
        <div className="search-table-wrapper">
          <DataTable
            columns={columns}
            data={filteredPersons}
            progressPending={loading}
            progressComponent={<DataTableLoader />}
            pagination
            paginationPerPage={50}
            paginationComponentOptions={{ noRowsPerPage: true, rangeSeparatorText: 'of' }}
            paginationTotalRows={filteredPersons.length}
            highlightOnHover={false}
            pointerOnHover={false}
            striped={false}
            selectableRows
            selectableRowDisabled={(row) => Boolean(participantByPersonId[String(row?._id)])}
            selectableRowSelected={isRowSelected}
            clearSelectedRows={clearSelectedRows}
            responsive
            onSelectedRowsChange={(event) => {
              if (!isSyncingSelection.current) {
                setSelectedRows((currentRows) =>
                  sameSelectedPeople(currentRows, event.selectedRows) ? currentRows : event.selectedRows
                );
              }
            }}
            onRowClicked={handleRowClick}
            customStyles={searchTableStyles}
            dense={false}
          />
        </div>
      )}

      {selectedRows.length > 0 && (
        <div className="search-selection-footer">
          <div>
            <strong>{selectedRows.length} participant{selectedRows.length === 1 ? '' : 's'} selected</strong>
            <button type="button" className="search-selection-clear" onClick={clearSelection}>
              Clear selection
            </button>
          </div>
          <button type="button" className="btn search-assign-submit-btn" onClick={() => setShowAssignModal(true)}>
            Assign to Competition
          </button>
        </div>
      )}

      {showAssignModal && (
        <AssignParticipantsModal
          apiUrl={API_URL}
          competitionId={competitionId}
          selectedParticipants={selectedRows}
          groups={groups}
          emsRoles={emsRoles}
          invitationTypes={invitationTypes}
          accreditationCategories={accreditationCategories}
          onRemoveParticipant={removeSelectedParticipant}
          onClose={() => setShowAssignModal(false)}
          onAssigned={handleAssigned}
        />
      )}

      {profileChooserItems && (
        <OpenAssignedProfileModal
          profiles={profileChooserItems}
          onSelect={handleChooserSelect}
          onClose={() => setProfileChooserItems(null)}
        />
      )}
    </div>
  );
};

export default SearchTable;
